import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { cn, convertStringToNumber } from "@/libs/utils";
import * as AccordionPrimitive from "@radix-ui/react-accordion";
import {
  ChevronDown,
  ChevronRight,
  FileIcon,
  FolderIcon,
  FolderOpenIcon,
  ListFilter,
} from "lucide-react";
import React, {
  createContext,
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";

type TreeViewElement = {
  id: string;
  name: string;
  isSelectable?: boolean;
  children?: TreeViewElement[];
};

type TreeContextProps = {
  selectedId: string | undefined;
  expendedItems: string[] | undefined;
  indicator: boolean;
  handleExpand: (id: string) => void;
  selectItem: (id: string) => void;
  setExpendedItems?: React.Dispatch<React.SetStateAction<string[] | undefined>>;
  openIcon?: React.ReactNode;
  closeIcon?: React.ReactNode;
  direction: "rtl" | "ltr";
};

const TreeContext = createContext<TreeContextProps | null>(null);

const useTree = () => {
  const context = useContext(TreeContext);
  if (!context) {
    throw new Error("useTree must be used within a TreeProvider");
  }
  return context;
};

interface TreeViewComponentProps extends React.HTMLAttributes<HTMLDivElement> {}

type Direction = "rtl" | "ltr" | undefined;

type TreeViewProps = {
  initialSelectedId?: string;
  indicator?: boolean;
  elements?: TreeViewElement[];
  initialExpendedItems?: string[];
  openIcon?: React.ReactNode;
  closeIcon?: React.ReactNode;
} & TreeViewComponentProps;

/**
 * Tree View Docs: {@link: https://shadcn-extension.vercel.app/docs/tree-view}
 */

const Tree = forwardRef<HTMLDivElement, TreeViewProps>(
  (
    {
      className,
      elements,
      initialSelectedId,
      initialExpendedItems,
      children,
      indicator = true,
      openIcon,
      closeIcon,
      dir,
      ...props
    },
    ref
  ) => {
    const [selectedId, setSelectedId] = useState<string | undefined>(
      initialSelectedId
    );
    const [expendedItems, setExpendedItems] = useState<string[] | undefined>(
      initialExpendedItems
    );

    const selectItem = useCallback((id: string) => {
      setSelectedId(id);
    }, []);

    const handleExpand = useCallback((id: string) => {
      setExpendedItems((prev) => {
        if (prev?.includes(id)) {
          return prev.filter((item) => item !== id);
        }
        return [...(prev ?? []), id];
      });
    }, []);

    const expandSpecificTargetedElements = useCallback(
      (elements?: TreeViewElement[], selectId?: string) => {
        if (!elements || !selectId) return;
        const findParent = (
          currentElement: TreeViewElement,
          currentPath: string[] = []
        ) => {
          const isSelectable = currentElement.isSelectable ?? true;
          const newPath = [...currentPath, currentElement.id];
          if (currentElement.id === selectId) {
            if (isSelectable) {
              setExpendedItems((prev) => [...(prev ?? []), ...newPath]);
            } else {
              if (newPath.includes(currentElement.id)) {
                newPath.pop();
                setExpendedItems((prev) => [...(prev ?? []), ...newPath]);
              }
            }
            return;
          }
          if (
            isSelectable &&
            currentElement.children &&
            currentElement.children.length > 0
          ) {
            currentElement.children.forEach((child) => {
              findParent(child, newPath);
            });
          }
        };
        elements.forEach((element) => {
          findParent(element);
        });
      },
      []
    );

    useEffect(() => {
      if (initialSelectedId) {
        expandSpecificTargetedElements(elements, initialSelectedId);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialSelectedId, elements]);

    const direction = dir === "rtl" ? "rtl" : "ltr";

    return (
      <TreeContext.Provider
        value={{
          selectedId,
          expendedItems,
          handleExpand,
          selectItem,
          setExpendedItems,
          indicator,
          openIcon,
          closeIcon,
          direction,
        }}
      >
        <div className={cn("", className)}>
          <ScrollArea
            ref={ref}
            className="relative h-full"
            dir={dir as Direction}
          >
            <AccordionPrimitive.Root
              {...props}
              type="multiple"
              defaultValue={expendedItems}
              value={expendedItems}
              className="flex flex-col gap-1"
              onValueChange={(value) =>
                setExpendedItems((prev) => [...(prev ?? []), value[0]])
              }
              dir={dir as Direction}
            >
              {children}
            </AccordionPrimitive.Root>
          </ScrollArea>
        </div>
      </TreeContext.Provider>
    );
  }
);

Tree.displayName = "Tree";

const TreeIndicator = forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => {
  const { direction } = useTree();

  return (
    <div
      dir={direction}
      ref={ref}
      className={cn(
        "h-full w-px bg-secondary/30 absolute left-1.5 rtl:right-1.5 py-3 rounded-md hover:bg-slate-300 duration-300 ease-in-out",
        className
      )}
      {...props}
    />
  );
});

TreeIndicator.displayName = "TreeIndicator";

interface FolderComponentProps
  extends React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Item> {}

type FolderProps = {
  expendedItems?: string[];
  element: string;
  isSelectable?: boolean;
  isSelect?: boolean;
  projectId?: number;
  visibleChevron?: boolean;
  onFilterChange?: (value: string[], financements: string[]) => void;
  isSold?: boolean;
  type?: string;
  status?: number;
  isPersonnalized?: boolean;
  programContextId?: number;
} & FolderComponentProps;

const Folder = forwardRef<
  HTMLDivElement,
  FolderProps & React.HTMLAttributes<HTMLDivElement>
>(
  (
    {
      className,
      element,
      value,
      isSelectable = true,
      isSelect,
      children,
      projectId,
      visibleChevron = false,
      onFilterChange = null,
      isSold = false,
      type = "",
      status,
      isPersonnalized = false,
      programContextId,
      ...props
    },
    ref
  ) => {
    const {
      direction,
      handleExpand,
      expendedItems,
      setExpendedItems,
      openIcon,
      closeIcon,
    } = useTree();
    let bg = "background";
    switch (status) {
      case 0:
        bg = "gray-200";
        break;
      case 1:
        {
          bg = "orangeProgress";
        }
        break;
      case 2:
        bg = "verdeoblue";
        break;
      case 3:
        bg = "verdeogreen";
        break;
      case 4:
        bg = "verdeored";
        break;
      case 5:
        bg = "[#14ebfa]";
        break;
      case 6:
        bg = "verdeoblue";
        break;
      case 7:
        bg = "verdeogray";
        break;
      default:
        break;
    }
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();
    const idParams = convertStringToNumber(id);
    const page = window.location.pathname;

    const handleOpen = () => {
      const pageParent = page.search("submit") >= 1 ? "submit" : "dashboard";

      //On rédirige vers le program si on dans dashboard et qu'on est sur un projet
      if (page.includes("project") && pageParent == "dashboard") {
        if (value.includes("program")) {
          const idProgram = value.split("-")[1];
          window.location.href = `/${pageParent}/program/${idProgram}`;
        }
      }
      setExpendedItems((prev) => {
        if (value == "1") {
          navigate(`/${pageParent}/programs`);
          return ["1"];
        }
        // if (prev?.includes(value)) {
        //   return prev.filter((item) => item !== value);
        // }
        if (parseInt(value) >= 10 && pageParent === "submit") {
          navigate(`/submit/program/${idParams}?project=${projectId}&step=2`);
        } else if (value.includes("project") && pageParent === "dashboard") {
          const numericStatus = Number(status);
          const shouldOpenDynamicForm =
            isPersonnalized &&
            Number.isFinite(numericStatus) &&
            numericStatus !== 3;
          if (shouldOpenDynamicForm) {
            const search = new URLSearchParams();
            if (programContextId) {
              search.set("programId", String(programContextId));
            }
            if (projectId) {
              search.set("projectId", String(projectId));
            }
            window.location.href = `/dashboard/project/create-custom?${search.toString()}`;
          } else {
            window.location.href = `/dashboard/project/${projectId}?step=1`;
          }
          return [`project-${projectId}`];
        } else if (value.includes("program") && pageParent === "dashboard") {
          window.location.href = `/dashboard/program/${projectId}`;
          return [`program-${projectId}`];
        }
        return ["1", "2", "3", value];
      });
    };

    const [selectedStatus, setSelectedStatus] = useState([]);
    const [selectedFinancements, setSelectedFinancements] = useState([]);

    const optionStatus = [
      "Non commencé",
      "En cours",
      "En attente de validation",
      "Validé",
      "Rejeté",
      "Clôturé",
    ];

    const optionFinancements = [
      "Pas encore financé",
      "Partiellement financé",
      "Entierement financé",
    ];

    const toggleStatus = (item) => {
      setSelectedStatus((prev) =>
        prev.includes(item) ? prev.filter((i) => i !== item) : [...prev, item]
      );
    };

    const toggleFinancement = (item) => {
      setSelectedFinancements((prev) =>
        prev.includes(item) ? prev.filter((i) => i !== item) : [...prev, item]
      );
    };

    useEffect(() => {
      // console.log('nandalo...', onFilterChange, selectedFinancements)
      if (onFilterChange) {
        onFilterChange(selectedStatus, selectedFinancements);
      }
    }, [selectedStatus, selectedFinancements]);

    return (
      <AccordionPrimitive.Item
        {...props}
        value={value}
        ref={ref}
        className="relative mt-0 h-full overflow-hidden"
      >
        <AccordionPrimitive.Trigger
          className={cn(
            `relative flex items-center gap-1 text-sm rounded-md w-full group`,
            className,
            {
              "bg-muted rounded-md": isSelect && isSelectable,
              "cursor-pointer": isSelectable,
              "cursor-not-allowed opacity-50": !isSelectable,
            }
          )}
          disabled={!isSelectable}
          onClick={() => {
            if (page.search("submit") >= 1) {
              handleOpen();
            } else {
              handleExpand(value);
            }
          }}
        >
          {visibleChevron &&
            (expendedItems?.includes(value)
              ? openIcon ?? (
                  <ChevronDown
                    size={20}
                    className={` text-muted-foreground ${
                      value === "1" ? "mr-[-1px]" : "mr-1"
                    }`}
                  />
                )
              : closeIcon ?? (
                  <ChevronRight
                    size={20}
                    className={` text-muted-foreground ${
                      value === "1" ? "mr-[-1px]" : "mr-1"
                    }`}
                  />
                ))}
          {expendedItems?.includes(value)
            ? openIcon ?? (
                <FolderOpenIcon className="size-5 text-muted-foreground group-hover:text-primary" />
              )
            : closeIcon ?? (
                <FolderIcon className="size-5 text-muted-foreground group-hover:text-primary" />
              )}
          <span
            title={element}
            className={`line-clamp-1 flex-1 text-left text-[13px] leading-4 text-muted-foreground hover:text-primary hover:opacity-100`}
            style={{ maxWidth: "calc(100% - 60px)" }}
            onClick={() => handleOpen()}
          >
            {element}
          </span>
          {value === "1" && (
            <Popover>
              <PopoverTrigger asChild className="absolute right-1 top-1">
                <ListFilter
                  size={18}
                  color={selectedStatus.length > 0 ? "#FF9500" : "#878787"}
                />
              </PopoverTrigger>
              <PopoverContent className="text-xs">
                <div className="bg-white w-full max-w-sm">
                  <h2 className="text-md font-semibold mb-4">
                    Filtrer par statut
                  </h2>
                  <ul className="space-y-2">
                    {optionStatus.map((item) => (
                      <li key={item} className="flex items-center">
                        <input
                          type="checkbox"
                          id={item}
                          checked={selectedStatus.includes(item)}
                          onChange={() => toggleStatus(item)}
                          className="mr-2 accent-blue-600"
                        />
                        <label htmlFor={item} className="text-gray-700">
                          {item}
                        </label>
                      </li>
                    ))}
                  </ul>
                  <h2 className="text-md font-semibold mb-4 mt-5">
                    Filtrer par financement
                  </h2>
                  <ul className="space-y-2">
                    {optionFinancements.map((item) => (
                      <li key={item} className="flex items-center">
                        <input
                          type="checkbox"
                          id={item}
                          checked={selectedFinancements.includes(item)}
                          onChange={() => toggleFinancement(item)}
                          className="mr-2 accent-blue-600"
                        />
                        <label htmlFor={item} className="text-gray-700">
                          {item}
                        </label>
                      </li>
                    ))}
                  </ul>
                </div>
              </PopoverContent>
            </Popover>
          )}
          {isSold &&
            ((type === "program" && !expendedItems?.includes(value)) ||
              type !== "program") && (
              <span
                className={
                  "absolute right-[2px] size-1 rounded-full bg-red-500 animate-ping " +
                  (type === "program" ? "top-[12px]" : "top-[5px]")
                }
              ></span>
            )}
          {status != undefined && (
            <div
              className={`absolute left-[40px] bottom-[1px] size-2.5 rounded-full bg-${bg} border-2 border-background`}
            ></div>
          )}
        </AccordionPrimitive.Trigger>
        <AccordionPrimitive.Content className="relative mt-2 h-full overflow-hidden text-sm data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down">
          {/* {element && indicator && <TreeIndicator aria-hidden="true" />} */}
          <AccordionPrimitive.Root
            dir={direction}
            type="multiple"
            className="folder-arrow ml-5 flex flex-col gap-1 py-1 rtl:mr-5"
            defaultValue={expendedItems}
            value={expendedItems}
            onValueChange={(value) => {
              setExpendedItems?.((prev) => [...(prev ?? []), value[0]]);
            }}
          >
            {children}
          </AccordionPrimitive.Root>
        </AccordionPrimitive.Content>
      </AccordionPrimitive.Item>
    );
  }
);

Folder.displayName = "Folder";

const File = forwardRef<
  HTMLButtonElement,
  {
    value: string;
    handleSelect?: (id: string) => void;
    isSelectable?: boolean;
    isSelect?: boolean;
    fileIcon?: React.ReactNode;
  } & React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Trigger>
>(
  (
    {
      value,
      className,
      isSelectable = true,
      isSelect,
      fileIcon,
      children,
      ...props
    },
    ref
  ) => {
    const { direction, selectedId, selectItem } = useTree();
    const isSelected = isSelect ?? selectedId === value;
    return (
      <AccordionPrimitive.Item value={value} className="relative">
        <AccordionPrimitive.Trigger
          ref={ref}
          {...props}
          dir={direction}
          disabled={!isSelectable}
          aria-label="File"
          className={cn(
            "flex items-center gap-1 cursor-pointer text-sm pr-1 rtl:pl-1 rtl:pr-0 rounded-md  duration-200 ease-in-out",
            {
              "": isSelected && isSelectable,
            },
            isSelectable ? "cursor-pointer" : "cursor-not-allowed",
            className
          )}
          onClick={() => selectItem(value)}
        >
          {fileIcon ?? <FileIcon className="size-6" />}
          {children}
        </AccordionPrimitive.Trigger>
      </AccordionPrimitive.Item>
    );
  }
);

File.displayName = "File";

const CollapseButton = forwardRef<
  HTMLButtonElement,
  {
    elements: TreeViewElement[];
    expandAll?: boolean;
  } & React.HTMLAttributes<HTMLButtonElement>
>(({ elements, expandAll = false, children, ...props }, ref) => {
  const { expendedItems, setExpendedItems } = useTree();

  const expendAllTree = useCallback((elements: TreeViewElement[]) => {
    const expandTree = (element: TreeViewElement) => {
      const isSelectable = element.isSelectable ?? true;
      if (isSelectable && element.children && element.children.length > 0) {
        setExpendedItems?.((prev) => [...(prev ?? []), element.id]);
        element.children.forEach(expandTree);
      }
    };

    elements.forEach(expandTree);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const closeAll = useCallback(() => {
    setExpendedItems?.([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (expandAll) {
      expendAllTree(elements);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expandAll]);

  return (
    <Button
      variant={"ghost"}
      className="absolute bottom-1 right-2 h-8 w-fit p-1"
      onClick={
        expendedItems && expendedItems.length > 0
          ? closeAll
          : () => expendAllTree(elements)
      }
      ref={ref}
      {...props}
    >
      {children}
      <span className="sr-only">Toggle</span>
    </Button>
  );
});

CollapseButton.displayName = "CollapseButton";

export { CollapseButton, File, Folder, Tree, type TreeViewElement };
